import React, { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import { saveCurrentLocation } from './clients/ApiClients';
import {
  clearAllTokens,
  getToken,
  sessionStatus,
  subscribeToToken,
  tokenLooksLive,
} from './utils/TokenUtils';

/** How often an open, visible tab asks the server whether its session is
 *  still good (#635). Returning to a tab after longer than this checks at
 *  once. */
export const SESSION_CHECK_EVERY_MS = 5 * 60 * 1000;

type GuardState = 'checking' | 'signed-in' | 'signed-out';

/**
 * The one guard over every signed-in page (#635), mounted once as a layout
 * route so moving between pages never remounts it.
 *
 * It used to wrap each route separately and hid the whole page behind a
 * spinner until `/auth/verify` answered, on every full load (reload, new
 * tab, pasted link): 57ms in a browser, 125–140ms from curl. Moving
 * between pages did not re-check — React Router keeps the same guard
 * instance across sibling routes — but that was an accident of shape, and
 * one layout route makes it explicit.
 *
 * - **A token that has not expired renders at once**, and the server is
 *   asked in the background. Only a missing or expired token waits,
 *   because it may need a refresh or a redirect.
 * - **The server is asked again** every `SESSION_CHECK_EVERY_MS` while the
 *   tab is visible, and when the tab comes back after longer than that.
 *   A refusal signs out, remembering where to come back to, as a 401 does.
 *   No answer does not: a dropped connection is not a revoked session.
 *
 * This is a redirect for the UI, not the security boundary. Every request
 * is authenticated by the API, whose 401 already sends people to sign in.
 */
const ProtectedRoute: React.FC = () => {
  const [state, setState] = useState<GuardState>(() =>
    tokenLooksLive() ? 'signed-in' : 'checking'
  );
  const stateRef = useRef(state);
  stateRef.current = state;
  const lastChecked = useRef(0);

  useEffect(() => {
    let live = true;
    let inFlight = false;

    const check = async () => {
      if (inFlight) return;
      inFlight = true;
      const status = await sessionStatus(getToken());
      inFlight = false;
      lastChecked.current = Date.now();
      if (!live) return;
      if (status === 'valid') {
        setState('signed-in');
      } else if (status === 'invalid' || stateRef.current === 'checking') {
        // With nothing rendered yet there is no session to keep showing.
        if (status === 'invalid') {
          saveCurrentLocation();
          clearAllTokens();
        }
        setState('signed-out');
      }
    };

    check();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') check();
    }, SESSION_CHECK_EVERY_MS);
    const onVisible = () => {
      if (
        document.visibilityState === 'visible' &&
        Date.now() - lastChecked.current >= SESSION_CHECK_EVERY_MS
      ) {
        check();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    // Signing out elsewhere in this tab clears the token.
    const unsubscribe = subscribeToToken(() => {
      if (!getToken()) setState('signed-out');
    });

    return () => {
      live = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      unsubscribe();
    };
  }, []);

  if (state === 'checking') return <LoadingSpinner />;
  if (state === 'signed-out') return <Navigate to="/login" replace />;
  return <Outlet />;
};

export default ProtectedRoute;
